import React, { useEffect, useMemo, useState } from 'react';
import { Collapse } from '../Collapse';
import Select from 'react-select';
import {
    Dictionary,
    IDropDownValue,
    IDropDownValueOfferType,
    IDropDownValueSectors,
    IGroupedOption,
    IOffersFilterParams,
    OffreFilter,
} from '../../Models';
import { FloatLabel } from '../FloatLabel';
import AsyncSelect from 'react-select/async';
import { Advertissement } from '../Advertissement';
import { ReferenceService, JobSeekerService } from '../../Services';
import {
    IReferenceLocalities,
    IReferenceRegimeTravail,
} from '../../Models/Reference';
import type { TreeSelectProps } from 'antd';
import { TreeSelect } from 'antd';

type TreeNode = NonNullable<TreeSelectProps['treeData']>[number];

// To avoid react-select named type import issues
type LocalitiesChangeHandler = NonNullable<
    React.ComponentProps<
        typeof AsyncSelect<IReferenceLocalities, true, any>
    >['onChange']
>;
type LocalitiesNewValue = Parameters<LocalitiesChangeHandler>[0];

const normalizeOfferTypeTree = (
    apiData: IDropDownValueOfferType[]
): TreeNode[] =>
    apiData.map((parent, parentIndex) => {
        const parentKey = `parent-${parentIndex}`;
        return {
            title: parent.title,
            key: parentKey,
            value: parentKey,
            children:
                parent.children?.map((child, childIndex) => {
                    const childKey =
                        child.key?.[0] ?? `${parentKey}-child-${childIndex}`;
                    const childValue = child.value?.[0] ?? childKey;
                    return {
                        title: child.title,
                        key: childKey,
                        value: childValue,
                    };
                }) ?? [],
        };
    });

const dateMinus = (daysToRemove: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() - daysToRemove);
    return date;
};

interface IOfferSearchProps {
    offersFilterParams: IOffersFilterParams;
    refineResults: string;
    offerCreateDate: string;
    queryParams: any[];
    domains: IDropDownValue[];
    getDomains: () => Promise<void>;
    sectors: IDropDownValueSectors[];
    getSectors: () => Promise<void>;
    getOffersType: () => Promise<void>;
    dropdownValueDomains: IDropDownValue[];
    offerTypeFilter: IDropDownValueOfferType[];
    sectorFilter: IDropDownValueSectors[];
    location: string;
    workTime: string;
    language: string;
    selectedLocalities: IReferenceLocalities[];
    level: string;
    oneLevel: string;
    getOffers: (
        searchParams: IOffersFilterParams,
        isFilter: boolean,
        setOffersLoadedCount: number,
        moreOption?: any
    ) => void;
    all: string;
    day: string;
    week: string;
    month: string;
    belgium: string;
    foreign: string;
    fullTime: string;
    partialTime: string;
    with: string;
    without: string;
    withConvention: string;
    offerSourceTitle: string;
    offerSourceEmployment: string;
    offerSourceStudent: string;
    dictSeeJobSeekerOffers: string;
    dictRemoveFilter: string;
    dictIsCovidOnly: string;
    showCovidOffers: string;
    localityList: (
        localityParams?: string,
        selectedLocalities?: IReferenceLocalities[]
    ) => Promise<IGroupedOption[]>;
    domainSelectId: string;
    dictSelectedDomain: string;
    dictLast30Days: string;
    dictLast7Days: string;
    dictAllOffers: string;
    dictWorkFamily: string;
    dictSector: string;
    dictWorkFamilies: string;
    dictNbSelectedDomain: string;
    dictNbSelectedDomains: string;
    dictSearch: string;
    searchParamsTypeActiris: boolean;
    dictLocalitiesDefaultMessage: string;
    dictLocations: string;
    dictAdvertTitle: string;
    dictAdvertDescription: string;
    dictAdvertLabel: string;
    dictTempHospitalOffers: string;
    isSjabloons: string;
    dictLocalities: string;
    dictLanguageInformation: string;
    dictLanguageMoreInformation: string;
    dictOnFocus: string;
    dictKeywordTitleOnly: string;
    dictActirisOffers: string;
    dictHandicapOffers: string;
    dictVdabOffers: string;
    dictFilterShowOnly: string;
    dictOthersOffers: string;
    dictZoneBelgium: string;
    dictTypeOfferStudent: string;
    dictTypeOfferStage: string;
    alertUrl: string;
    alertTitle: string;
    alertDescription: string;
    alertEnabled: boolean;
    disableResultFilter: boolean;
}

const { SHOW_ALL } = TreeSelect;

export const OfferSearchFilter: React.FC<IOfferSearchProps> = (props) => {
    const publishLanguageList: Dictionary<string | null, string>[] = [
        { value: 'Tout', label: props.all },
        { value: 'Fr', label: 'FR' },
        { value: 'Nl', label: 'NL' },
    ];

    const zoneList: Dictionary<string, string>[] = [
        { value: 'Tout', label: props.all },
        { value: 'Belgique', label: props.belgium },
        { value: 'Etranger', label: props.foreign },
    ];

    const publishedDateDictionary = [
        { value: null, label: props.dictAllOffers },
        { value: new Date(), label: props.day },
        { value: dateMinus(7), label: props.dictLast7Days },
        { value: dateMinus(30), label: props.dictLast30Days },
    ];

    const offersType: TreeNode[] =
        props.offerTypeFilter?.length > 0
            ? normalizeOfferTypeTree(props.offerTypeFilter)
            : [];

    const [regimesTravail, setRegimesTravail] = useState<
        IReferenceRegimeTravail[]
    >([]);
    const [regimesTravailDropdown, setRegimesTravailDropdown] = useState<
        Dictionary<string, string>[]
    >([]);

    const [inputValue, setInputValue] = useState('');

    const { domains, getDomains, sectors, getSectors, getOffersType } = props;

    useEffect(() => {
        const fetchRegimes = async () => {
            try {
                const regimes = await ReferenceService.getAllRegimeTravail();
                regimes.unshift({ id: 0, name: 'Tout', libelle: props.all });
                const dropdown = regimes
                    .filter((e) => e.id === 66 || e.id === 74 || e.id === 0)
                    .map((e) => ({ value: e.name, label: e.libelle }));
                setRegimesTravailDropdown(dropdown);
                setRegimesTravail(regimes);
            } catch (error) {
                console.error('Error fetching regimes:', error);
            }
        };
        void fetchRegimes();
    }, [props.all]);

    useEffect(() => {
        if (!domains.length) {
            void getDomains();
        }
    }, [domains.length, getDomains]);

    useEffect(() => {
        if (!sectors.length) {
            void getSectors();
        }
    }, [sectors.length, getSectors]);

    useEffect(() => {
        if (!offersType.length) {
            void getOffersType();
        }
    }, [offersType.length, getOffersType]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [selectedPublishedDate, setSelectedPublishedDate] = useState(
        () => publishedDateDictionary[0]
    );

    const [selectedZone, setSelectedZone] = useState<Dictionary<
        string,
        string
    > | null>(() => {
        const localisation = props.offersFilterParams.offreFilter.localisation;
        if (localisation) {
            return zoneList.find((e) => e.value === localisation) ?? null;
        }
        return null;
    });

    const [selectedPublishedLanguage, setSelectedPublishedLanguage] =
        useState<Dictionary<string | null, string> | null>(
            () => publishLanguageList[0]
        );

    const [selectedLocalities, setSelectedLocalities] = useState<
        IReferenceLocalities[]
    >(() => props.selectedLocalities ?? []);

    const [selectedDomains, setSelectedDomains] = useState<string[]>(
        () =>
            props.offersFilterParams.offreFilter.domainesImt?.filter(Boolean) ??
            []
    );

    const [selectedTypeOffer, setSelectedTypeOffer] = useState<string[]>(
        () =>
            props.offersFilterParams.offreFilter.codesContrat?.filter(
                (e: string) => e && e !== ''
            ) ?? []
    );

    const [selectedSector, setSelectedSector] = useState<number[]>(
        () =>
            props.offersFilterParams.offreFilter.secteursPanorama
                ?.filter(Boolean)
                .map(Number) ?? []
    );

    const [selectedDurationValue, setSelectedDurationValue] = useState<
        string | null
    >(() => props.offersFilterParams.offreFilter.regimesTravail?.[0] ?? null);

    const selectedDuration = useMemo<Dictionary<string, string> | null>(() => {
        if (!selectedDurationValue) return null;

        const match = regimesTravail.find(
            (r) => r.name === selectedDurationValue
        );
        if (!match) return null;

        return { value: match.name, label: match.libelle };
    }, [selectedDurationValue, regimesTravail]);

    const [onTitleOnly, setOnTitleOnly] = useState<'Titre' | 'Partout'>(
        props.offersFilterParams.offreFilter.keywordSearchType || 'Partout'
    );

    const [isOffreActiris, setIsOffreActiris] = useState(
        props.offersFilterParams.offreFilter.isOffreActiris || false
    );
    const [isOffreVdabForem, setIsOffreVdabForem] = useState(
        props.offersFilterParams.offreFilter.isOffreVdabForem || false
    );
    const [isOfferPartner, setIsOfferPartner] = useState(
        props.offersFilterParams.offreFilter.isOfferPartner || false
    );
    const [isOffreHandicap, setIsOffreHandicap] = useState(false);

    const onSelectPublishedDate = (
        option: (typeof publishedDateDictionary)[number]
    ) => {
        setSelectedPublishedDate(option);
    };

    const onSelectSector = (selectedItem: number[] | null) => {
        setSelectedSector(selectedItem ?? []);
    };

    const onSelectZone = (selectedItem: Dictionary<string, string> | null) => {
        setSelectedZone(selectedItem);
        if (selectedItem?.value === 'Etranger') {
            setSelectedLocalities([]);
        }
    };

    const onSelectDuration = (option: Dictionary<string, string> | null) => {
        setSelectedDurationValue(option?.value ?? null);
    };

    const onSelectPublishedLanguage = (
        selectedItem: Dictionary<string | null, string> | null
    ) => {
        setSelectedPublishedLanguage(selectedItem);
    };

    const convertLocalitiesToPostalCode = (
        localities: IReferenceLocalities[]
    ): number[] => {
        const cp: number[] = localities.reduce(
            (all: number[], curr: IReferenceLocalities) =>
                all.concat(
                    Array.isArray(curr.codePostal)
                        ? curr.codePostal.map((e) => +e)
                        : [+curr.codePostal]
                ),
            []
        );
        return [...new Set(cp)];
    };

    const onSelectLocalities = (newValue: LocalitiesNewValue) => {
        setSelectedLocalities(Array.isArray(newValue) ? [...newValue] : []);
    };

    const onSelectDomain = (selectedItem: string[]) => {
        setSelectedDomains(selectedItem);
    };

    const handleToggleTitleOnly = () => {
        setOnTitleOnly((prev) => (prev === 'Partout' ? 'Titre' : 'Partout'));
    };

    const handleToggleIsOffreActiris = () => {
        setIsOffreActiris((prev) => !prev);
    };

    const handleToggleIsOffreVdabForem = () => {
        setIsOffreVdabForem((prev) => !prev);
    };

    const handleToggleIsOfferPartner = () => {
        setIsOfferPartner((prev) => !prev);
    };

    // const handleToggleIsOffreHandicap = () => {
    //     setIsOffreHandicap((prev) => !prev);
    // };

    const handleRemoveSelectedLocality = (
        item: IReferenceLocalities,
        event: React.MouseEvent
    ) => {
        event.preventDefault();
        setSelectedLocalities((prev) =>
            prev.filter((e) => e.value !== item.value)
        );
    };

    const collapsePanel = () => {
        setIsFilterOpen((prev) => !prev);
    };

    const onInputLocalityValue = async (inputValue: string) => {
        const result = await props.localityList(inputValue, selectedLocalities);
        const selectedIds = new Set(
            selectedLocalities.map((l) => l.value ?? `com-${l.communeId}`)
        );
        return result
            .map((group) => ({
                ...group,
                options: group.options
                    .filter((opt) => !selectedIds.has(opt.value))
                    .sort((a, b) => a.label.localeCompare(b.label)),
            }))
            .filter((group) => group.options.length > 0);
    };

    const launchSearch = () => {
        const updatedParams = { ...props.offersFilterParams };
        updatedParams.pageOption = { ...updatedParams.pageOption, page: 1 };

        updatedParams.offreFilter.dateDerniereModification =
            selectedPublishedDate?.value || null;
        updatedParams.offreFilter.secteursPanorama = selectedSector;
        updatedParams.offreFilter.localisation =
            (selectedZone?.value as OffreFilter['localisation']) ?? null;
        updatedParams.offreFilter.regimesTravail =
            !selectedDurationValue || selectedDurationValue === 'Tout'
                ? []
                : [selectedDurationValue];
        updatedParams.offreFilter.langue =
            selectedPublishedLanguage?.value === 'Tout' ||
            selectedPublishedLanguage?.value == null
                ? null
                : (selectedPublishedLanguage.value as OffreFilter['langue']);
        updatedParams.offreFilter.codesPostal =
            convertLocalitiesToPostalCode(selectedLocalities);
        updatedParams.offreFilter.domainesImt = selectedDomains;
        updatedParams.offreFilter.codesContrat = selectedTypeOffer;
        updatedParams.offreFilter.keywordSearchType = onTitleOnly;
        updatedParams.offreFilter.isOffreActiris = isOffreActiris;
        updatedParams.offreFilter.isOffreVdabForem = isOffreVdabForem;
        updatedParams.offreFilter.isOfferPartner = isOfferPartner;
        updatedParams.offreFilter.isOffreHandicap = false;

        props.getOffers(updatedParams, true, 0, {
            localities: selectedLocalities,
        });
    };

    return (
        <div className="col-12 col-lg-4 sticky-container">
            <div className="text-center d-block d-lg-none mb-4">
                <button
                    type="button"
                    className={`btn-primary btn-filter${isFilterOpen ? ' active' : ''}`}
                    aria-expanded={isFilterOpen}
                    onClick={collapsePanel}
                    aria-controls="filterSearch"
                >
                    <i
                        className={isFilterOpen ? 'icon-cross' : 'icon-filtre'}
                    />
                    {props.refineResults}
                </button>
            </div>

            <Collapse id="filterSearch" active={isFilterOpen} desktop={false}>
                <form className="filter-search" data-skip-captcha="true">
                    <h2>
                        <i className="icon-filtre" />
                        {props.refineResults}
                    </h2>

                    {/* Keyword search type */}
                    <div className="form-group form-group-checkbox">
                        <input
                            type="checkbox"
                            id="checkbox"
                            checked={onTitleOnly === 'Titre'}
                            onChange={handleToggleTitleOnly}
                        />
                        <label htmlFor="checkbox">
                            {props.dictKeywordTitleOnly}
                        </label>
                    </div>

                    {/* Offer type */}
                    {!props.searchParamsTypeActiris &&
                        offersType.length > 0 && (
                            <div className="form-group">
                                <FloatLabel
                                    active={true}
                                    label={props.offerSourceTitle}
                                    labelId="typesOfferFilter"
                                >
                                    <TreeSelect
                                        id="typesOfferFilter"
                                        aria-labelledby="typesOfferFilterLabel"
                                        treeData={offersType}
                                        value={selectedTypeOffer}
                                        onChange={setSelectedTypeOffer}
                                        treeCheckable
                                        showSearch
                                        showCheckedStrategy={SHOW_ALL}
                                        placeholder={props.offerSourceTitle}
                                        style={{ width: '100%' }}
                                    />
                                </FloatLabel>
                            </div>
                        )}

                    {/* Localities */}
                    <div className="form-group autocomplete-filter">
                        <FloatLabel
                            active={true}
                            label={props.dictLocalities}
                            labelId="localityFilter"
                            htmlFor="localityFilter"
                        >
                            <AsyncSelect<IReferenceLocalities, true>
                                inputId="localityFilter"
                                className="filter-select-container"
                                classNamePrefix="filter-select-no-icon"
                                isClearable
                                isMulti
                                cacheOptions
                                loadOptions={onInputLocalityValue}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                value={selectedLocalities}
                                onChange={onSelectLocalities}
                                inputValue={inputValue}
                                onInputChange={(value) => setInputValue(value)}
                                placeholder={props.dictLocalities}
                                noOptionsMessage={() =>
                                    props.dictLocalitiesDefaultMessage
                                }
                                components={{
                                    DropdownIndicator: () => null,
                                    IndicatorSeparator: () => null,
                                    ClearIndicator: () => null,
                                    MultiValue: () => (
                                        <span style={{ display: 'none' }} />
                                    ),
                                }}
                            />
                            {selectedLocalities.length > 0 &&
                                inputValue === '' && (
                                    <div className="selected-value">
                                        {selectedLocalities.length}{' '}
                                        {selectedLocalities.length > 1
                                            ? props.dictLocations
                                            : props.location}
                                    </div>
                                )}
                        </FloatLabel>
                    </div>

                    {/* Selected localities chips */}
                    {selectedLocalities.length > 0 && (
                        <div
                            id="gtm-localities"
                            className="chips gtm-localities"
                            data-gtmlocalities={selectedLocalities
                                .map(({ libelle }) => libelle)
                                .join(',')}
                        >
                            {selectedLocalities.map((item) => (
                                <span
                                    className="chip"
                                    key={`${item.arrondissementId}-${item.libelle}-${item.communeId}`}
                                >
                                    {item.label}
                                    <button
                                        type="button"
                                        className="btn-no-look"
                                        aria-label={props.dictRemoveFilter}
                                        onClick={(e) =>
                                            handleRemoveSelectedLocality(
                                                item,
                                                e
                                            )
                                        }
                                    >
                                        <i className="icon-cross" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Zone */}
                    <div className="form-group">
                        <FloatLabel
                            active={true}
                            label={props.location}
                            labelId="zoneFilter"
                            htmlFor="zoneFilter"
                        >
                            <Select
                                inputId="zoneFilter"
                                aria-labelledby="zoneFilter"
                                placeholder={props.location}
                                value={selectedZone}
                                options={zoneList}
                                onChange={onSelectZone}
                                className="filter-select-container"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                            />
                        </FloatLabel>
                    </div>

                    {/* Published date */}
                    <div className="form-group">
                        <FloatLabel
                            active={true}
                            label={props.offerCreateDate}
                            labelId="publishedDateFilter"
                            htmlFor="publishedDateFilter"
                        >
                            <Select
                                inputId="publishedDateFilter"
                                placeholder={props.offerCreateDate}
                                options={publishedDateDictionary}
                                value={selectedPublishedDate}
                                onChange={onSelectPublishedDate}
                                className="filter-select-container"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                            />
                        </FloatLabel>
                    </div>

                    {/* Work time */}
                    <div className="form-group">
                        <FloatLabel
                            active={true}
                            label={props.workTime}
                            labelId="durationFilter"
                            htmlFor="durationFilter"
                        >
                            <Select
                                inputId="durationFilter"
                                aria-labelledby="durationFilter"
                                placeholder={props.workTime}
                                value={selectedDuration}
                                options={regimesTravailDropdown}
                                onChange={onSelectDuration}
                                className="filter-select-container"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                            />
                        </FloatLabel>
                    </div>

                    {/* Domains */}
                    {!props.searchParamsTypeActiris &&
                        props.domains.length > 0 && (
                            <div className="form-group">
                                <FloatLabel
                                    active={true}
                                    label={props.dictWorkFamily}
                                    labelId="domainFilter"
                                    htmlFor="domainFilter"
                                >
                                    <TreeSelect
                                        treeData={props.domains}
                                        value={selectedDomains}
                                        onChange={onSelectDomain}
                                        treeCheckable={true}
                                        showSearch={true}
                                        showCheckedStrategy={SHOW_ALL}
                                        placeholder={props.dictWorkFamily}
                                        style={{ width: '100%' }}
                                    />
                                </FloatLabel>
                            </div>
                        )}

                    {/* Sectors */}
                    <div className="form-group">
                        <FloatLabel
                            active={true}
                            label={props.dictSector}
                            labelId="sectorDateFilter"
                        >
                            <TreeSelect
                                id="typesOfferFilter"
                                aria-labelledby="typesOfferFilterLabel"
                                treeData={props.sectors || []}
                                value={selectedSector}
                                onChange={onSelectSector}
                                treeCheckable={true}
                                showSearch={true}
                                showCheckedStrategy={SHOW_ALL}
                                placeholder={props.dictSector}
                                style={{ width: '100%' }}
                            />
                        </FloatLabel>
                    </div>

                    {/* Language */}
                    <div className="form-group">
                        <FloatLabel
                            active={true}
                            label={props.language}
                            labelId="publishedLanguageFilter"
                            htmlFor="publishedLanguageFilter"
                        >
                            <Select
                                inputId="publishedLanguageFilter"
                                aria-labelledby="publishedLanguageFilter"
                                placeholder={props.language}
                                value={selectedPublishedLanguage}
                                options={publishLanguageList}
                                onChange={onSelectPublishedLanguage}
                                className="filter-select-container"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                            />
                        </FloatLabel>
                    </div>

                    {selectedPublishedLanguage?.value !== 'Tout' &&
                        selectedPublishedLanguage?.value !== null && (
                            <div>
                                <p>{props.dictLanguageInformation}</p>
                            </div>
                        )}

                    {/* Sources filter */}
                    <h5>{props.dictFilterShowOnly}</h5>

                    <div className="form-group">
                        <div className="form-group switch">
                            <input
                                type="checkbox"
                                id="switchActirisOffers"
                                checked={isOffreActiris}
                                onChange={handleToggleIsOffreActiris}
                            />
                            <label htmlFor="switchActirisOffers">
                                {props.dictActirisOffers}
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="form-group switch">
                            <input
                                type="checkbox"
                                id="switchVdabOffers"
                                checked={isOffreVdabForem}
                                onChange={handleToggleIsOffreVdabForem}
                            />
                            <label htmlFor="switchVdabOffers">
                                {props.dictVdabOffers}
                            </label>
                        </div>
                    </div>

                    {/*<div className="form-group">*/}
                    {/*    <div className="form-group switch">*/}
                    {/*        <input*/}
                    {/*            type="checkbox"*/}
                    {/*            id="switchHandicapOffers"*/}
                    {/*            checked={isOffreHandicap}*/}
                    {/*            onChange={handleToggleIsOffreHandicap}*/}
                    {/*        />*/}
                    {/*        <label htmlFor="switchHandicapOffers">*/}
                    {/*            {props.dictHandicapOffers}*/}
                    {/*        </label>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    <div className="form-group">
                        <div className="form-group switch">
                            <input
                                type="checkbox"
                                id="switchOtherOffers"
                                checked={isOfferPartner}
                                onChange={handleToggleIsOfferPartner}
                            />
                            <label htmlFor="switchOtherOffers">
                                {props.dictOthersOffers}
                            </label>
                        </div>
                    </div>

                    <div className="form-group text-center">
                        <button
                            type="button"
                            onClick={launchSearch}
                            className="btn-primary"
                        >
                            {props.dictSearch}
                        </button>
                    </div>
                </form>

                <Advertissement
                    title={props.dictAdvertTitle}
                    description={props.dictAdvertDescription}
                    url="https://my.actiris.brussels/"
                    buttonLabel={props.dictAdvertLabel}
                />
            </Collapse>
        </div>
    );
};
